import { create } from "zustand";

// The acting-company context — spark's implementation of
// @adr SPARK-0002-company-account-model@1 §5 ("Profil-Switch"):
//
//   "die Person sieht ihre Firmen (aus ihren `company_membership`-Zeilen) und schaltet in eine
//    hinein. Technisch = kein Re-Login, kein neues Token, gleicher `sub`; die aktive `companyId`
//    ist ein UI-/Anzeigekontext, den Genesis-Mutations optional mitbekommen und gegen die
//    Membership (Ebene B) autorisieren. Keycloak sieht vom Switch nichts."
//
// THIS IS THE FIRST STATE IN SPARK THAT IS NOT A UI PREFERENCE, TRANSIENT FEATURE STATE OR A
// READ-ONLY SESSION MIRROR, so three rules exist that no other store here needs, and each is
// enforced somewhere a test can see it:
//
// 1. IT IS AN ID, NOT A COMPANY. Nothing renders from this value directly. `useCompanyContext`
//    resolves it against the `myCompanies` list genesis returns and produces a context only when
//    the server's own membership rows contain it — so the name on screen is always genesis's
//    answer, never the browser's. An id the caller has no membership for resolves to nothing and
//    is cleared.
//
// 2. IT AUTHORIZES NOTHING. Every per-company mutation carries its target company id from the
//    route or the page it was invoked on, and genesis re-checks a `company_membership` row with
//    `role = admin` for that target on every call (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization). The BFF proxy does no
//    authorization at all — it injects the bearer and forwards verbatim — so anything the browser
//    holds is attacker-controlled by construction. This value is display context and must never
//    become an argument to a write. `use-company-admin.ts` deliberately does not import this file.
//
// 3. CHANGING IT RESETS THE APOLLO CACHE. One normalized cache keyed by entity id, deduped across
//    consumers by design, would otherwise serve an entity read while acting as company A to a
//    reader acting as company B. The policy and its reasoning live in
//    ../graphql/company-context-cache.ts, which is where it is wired.
//
// NOT PERSISTED, deliberately. §5 asks for a display context and says nothing about outliving the
// tab's JS heap; keeping it in memory means there is no durable browser artifact whose only job is
// decoration, nothing to clear on logout, and no id from a previous session resolving against a
// fresh membership list. Re-entering costs one click from /c or from the company's own page.

interface CompanyContextState {
  /** The company the person is acting as, or `null` for their own person context. */
  activeCompanyId: string | null;
  enterCompany: (companyId: string) => void;
  leaveCompany: () => void;
}

export const useCompanyContextStore = create<CompanyContextState>()((set) => ({
  activeCompanyId: null,
  enterCompany: (companyId) => set({ activeCompanyId: companyId }),
  leaveCompany: () => set({ activeCompanyId: null }),
}));
